import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { Play } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { fetchJson } from '@/api/client'
import { keys } from '@/api/keys'
import { patchSettings, settingsQuery } from '@/api/settings'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Field, Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { toast } from '@/components/ui/toast'
import type { AgeBand, AgeBandId, StoryGenerationMode, VoiceRole, VoiceSetting } from '@/schema'

/**
 * The numbers that decide what a story for each age looks like (§4.6.1),
 * and which Kokoro voice each role speaks with.
 *
 * These are **editable presets, not code** — the generator's schema, the
 * validator and the wizard all read them at request time, so raising
 * "words per scene" for 5–6 changes the next story without a deploy. A
 * changed cap applies to the next `POST /api/stories` (§5.10).
 *
 * Generation mode, numeric presets and voices are editable here. Derived
 * flags below the presets show the effect of a change.
 */

const BAND_LABEL: Record<AgeBandId, string> = { '3-4': 'Ages 3–4', '5-6': 'Ages 5–6', '7-8': 'Ages 7–8' }

const ROLE_LABEL: Record<VoiceRole, string> = {
  narrator: 'Narrator',
  child: 'Child',
  small_animal: 'Small animal',
  big_animal: 'Big animal',
  adult: 'Grown-up',
  creature: 'Creature',
}

interface TtsVoice {
  id: string
  name?: string
  lang?: string
  gender?: string
}

/**
 * `GET /api/tts/voices` answers `{voices: string[]}` (Kokoro ids); older builds sent `{id, name}`
 * objects. Both become `TtsVoice[]` here, so the pickers are never blank (05-ux §3 #4).
 */
export function normalizeVoices(raw: unknown): TtsVoice[] {
  const list = raw && typeof raw === 'object' && Array.isArray((raw as { voices?: unknown }).voices) ? ((raw as { voices: unknown[] }).voices as unknown[]) : []
  const out: TtsVoice[] = []
  for (const v of list) {
    if (typeof v === 'string' && v) out.push({ id: v, name: voiceName(v) })
    else if (v && typeof v === 'object' && typeof (v as TtsVoice).id === 'string' && (v as TtsVoice).id) out.push({ ...(v as TtsVoice), name: (v as TtsVoice).name ?? voiceName((v as TtsVoice).id) })
  }
  return out
}

/** `af_heart` → "Heart (American, female)". */
export function voiceName(id: string): string {
  const m = /^([a-z])([a-z])_(.+)$/.exec(id)
  if (!m) return id
  const lang: Record<string, string> = { a: 'American', b: 'British', e: 'Spanish', f: 'French', h: 'Hindi', i: 'Italian', j: 'Japanese', p: 'Portuguese', z: 'Chinese' }
  const gender = m[2] === 'f' ? 'female' : m[2] === 'm' ? 'male' : ''
  const base = m[3]!.replace(/[_-]+/g, ' ')
  const nice = base.charAt(0).toUpperCase() + base.slice(1)
  const parts = [lang[m[1]!], gender].filter(Boolean).join(', ')
  return parts ? `${nice} (${parts})` : nice
}

async function ttsVoicesQueryFn(signal?: AbortSignal): Promise<{ voices: TtsVoice[] }> {
  return { voices: normalizeVoices(await fetchJson<unknown>('/api/tts/voices', { signal, query: { provider: 'kokoro' } })) }
}

type NumericPath =
  | { kind: 'range'; key: 'total_words' | 'scenes' | 'words_per_scene'; label: string; hint?: string }
  | { kind: 'int'; key: 'max_words_per_sentence' | 'max_sentences_per_line' | 'max_cast' | 'max_props' | 'max_heroes' | 'vocab_tier'; label: string; hint?: string }
  | { kind: 'float'; key: 'tts_speed' | 'hold_after_line_s'; label: string; hint?: string }

const FIELDS: NumericPath[] = [
  { kind: 'range', key: 'total_words', label: 'Words in the whole story' },
  { kind: 'range', key: 'scenes', label: 'Scenes' },
  { kind: 'range', key: 'words_per_scene', label: 'Words per scene' },
  { kind: 'int', key: 'max_words_per_sentence', label: 'Longest sentence (words)' },
  { kind: 'int', key: 'max_sentences_per_line', label: 'Sentences per spoken line' },
  { kind: 'int', key: 'max_cast', label: 'Speaking heroes', hint: 'A quiet friend may be added beyond this when the band allows it.' },
  { kind: 'int', key: 'max_props', label: 'Props' },
  { kind: 'int', key: 'max_heroes', label: 'Hero cards in the wizard' },
  { kind: 'int', key: 'vocab_tier', label: 'Stretch words per scene' },
  { kind: 'float', key: 'tts_speed', label: 'Reading speed', hint: '1 is the voice service’s normal pace; 0.9 is a little slower.' },
  { kind: 'float', key: 'hold_after_line_s', label: 'Pause after each line (seconds)' },
]

export function StorySettingsForm() {
  const qc = useQueryClient()
  const settings = useQuery(settingsQuery)
  const [bands, setBands] = useState<AgeBand[] | null>(null)
  const [voices, setVoices] = useState<Partial<Record<VoiceRole, VoiceSetting>> | null>(null)
  const [generationMode, setGenerationMode] = useState<StoryGenerationMode>('bones')

  useEffect(() => {
    if (settings.data) {
      setGenerationMode(settings.data.story.generation_mode ?? 'bones')
      setBands(settings.data.story.age_bands.map((b) => ({ ...b })))
      setVoices({ ...settings.data.story.voices })
    }
  }, [settings.data?.story])

  const ttsVoices = useQuery({
    queryKey: keys.tts.voices(),
    queryFn: ({ signal }) => ttsVoicesQueryFn(signal),
    staleTime: 5 * 60_000,
    retry: 0,
  })

  const save = useMutation({
    mutationFn: () => patchSettings({ story: { generation_mode: generationMode, age_bands: bands ?? [], voices: voices ?? {} } }),
    onSuccess: (next) => {
      qc.setQueryData(keys.settings(), next)
      void qc.invalidateQueries({ queryKey: ['stories', 'presets'] })
      toast.success('Saved', { description: 'The next story uses these.' })
    },
    onError: (e) => toast.error('Could not save', { description: e instanceof Error ? e.message : String(e) }),
  })

  if (settings.isPending || (!settings.isError && (!bands || !voices))) {
    return (
      <section className="card p-5">
        <p className="text-sm text-ink-2">Loading story settings…</p>
      </section>
    )
  }
  if (settings.isError) {
    return (
      <section className="card p-5" role="alert">
        <p className="text-sm text-ink-2">Could not load story settings. {settings.error instanceof Error ? settings.error.message : ''}</p>
      </section>
    )
  }
  if (!bands || !voices) return null

  /** Replace one numeric field of one band; everything else is left alone. */
  const patchBand = (bandId: AgeBandId, key: string, value: number | { min: number; max: number }) => {
    setBands((prev) => (prev ? prev.map((b) => (b.band === bandId ? ({ ...b, [key]: value } as AgeBand) : b)) : prev))
  }

  return (
    <section className="card p-5" aria-labelledby="story-settings-heading">
      <h2 id="story-settings-heading" className="text-lg font-semibold text-ink">
        Stories
      </h2>
      <p className="mt-1 text-sm text-ink-2">How long a story is, how many characters can speak, and who sounds like what.</p>

      <fieldset className="mt-5" aria-describedby="generation-mode-hint">
        <legend className="text-sm font-semibold text-ink">Story generation mode</legend>
        <p id="generation-mode-hint" className="mt-1 text-sm text-ink-2">Choose how characters move in new stories. Existing stories keep their original mode.</p>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          {([
            { value: 'bones', label: 'Bones', hint: 'Use puppets with bones and their learned movements.' },
            { value: 'spritesheet', label: 'Sprite sheets', hint: 'Original character clips, with bone-animated objects and scenery for interactions.' },
          ] as const).map((mode) => (
            <label key={mode.value} className="flex cursor-pointer items-start gap-3 rounded-xl border border-line p-4 has-[:checked]:border-accent has-[:checked]:bg-accent-soft">
              <input type="radio" name="generation_mode" value={mode.value} checked={generationMode === mode.value} onChange={() => setGenerationMode(mode.value)} className="mt-1 size-4 shrink-0 accent-accent focus-visible:focus-ring" />
              <span><span className="block font-medium text-ink">{mode.label}</span><span className="mt-1 block text-sm text-ink-2">{mode.hint}</span></span>
            </label>
          ))}
        </div>
        <Link to="/sprites" className="mt-3 inline-flex min-h-11 items-center text-sm font-medium text-ink underline underline-offset-4 focus-visible:focus-ring">Open sprite editor</Link>
      </fieldset>

      <Tabs defaultValue={bands[0]?.band ?? '3-4'} className="mt-4">
        <TabsList>
          {bands.map((b) => (
            <TabsTrigger key={b.band} value={b.band}>
              {BAND_LABEL[b.band]}
            </TabsTrigger>
          ))}
        </TabsList>

        {bands.map((band) => (
          <TabsContent key={band.band} value={band.band} className="pt-4">
            <div className="grid gap-4 sm:grid-cols-2">
              {FIELDS.map((f) =>
                f.kind === 'range' ? (
                  <Field key={f.key} label={f.label} hint={f.hint}>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min={0}
                        value={band[f.key].min}
                        aria-label={`${f.label} minimum`}
                        onChange={(e) => patchBand(band.band, f.key, { ...band[f.key], min: toInt(e.target.value) })}
                      />
                      <span className="text-sm text-ink-3">to</span>
                      <Input
                        type="number"
                        min={0}
                        value={band[f.key].max}
                        aria-label={`${f.label} maximum`}
                        onChange={(e) => patchBand(band.band, f.key, { ...band[f.key], max: toInt(e.target.value) })}
                      />
                    </div>
                  </Field>
                ) : (
                  <Field key={f.key} label={f.label} hint={f.hint}>
                    <Input
                      type="number"
                      min={0}
                      step={f.kind === 'float' ? 0.1 : 1}
                      value={band[f.key]}
                      aria-label={f.label}
                      onChange={(e) => patchBand(band.band, f.key, f.kind === 'float' ? toFloat(e.target.value) : toInt(e.target.value))}
                    />
                  </Field>
                ),
              )}
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
              <Badge tone="neutral">Questions {band.questions_default ? 'on' : 'off'} by default</Badge>
              <Badge tone="neutral">Subtitles {band.subtitles_default ? 'on' : 'off'} by default</Badge>
              <Badge tone="neutral">{band.pass_b_mode === 'all_scenes' ? 'All scenes in one call' : 'One call per scene'}</Badge>
              <Badge tone="neutral">Walks as “{band.walk_speed.name}”</Badge>
              {band.refrain.required ? <Badge tone="neutral">Refrain ×{band.refrain.min_count}</Badge> : null}
              {band.quiet_friend ? <Badge tone="neutral">A quiet friend is allowed</Badge> : null}
            </div>
            <p className="mt-2 text-xs text-ink-3">Beats: {band.beats.join(' → ')}</p>
          </TabsContent>
        ))}
      </Tabs>

      <h3 className="mt-6 text-sm font-semibold text-ink">Voices</h3>
      <p className="mt-1 text-xs text-ink-3">
        {settings.data?.tts?.provider === 'fish'
          ? 'Fish Audio uses the configured Fish voice for all roles. These Kokoro voices stay saved for switching back; Listen samples the active provider.'
          : ttsVoices.isError ? 'The narration service is not answering yet, so the voice list is unavailable — the ids below still apply.' : 'Each role narrates with one Kokoro voice.'}
      </p>

      <div className="mt-3 flex flex-col gap-3">
        {(Object.keys(ROLE_LABEL) as VoiceRole[]).map((role) => {
          const v = voices[role]
          if (!v) return null
          return (
            <div key={role} className="grid items-end gap-3 rounded-xl border border-line p-3 sm:grid-cols-[1fr_8rem_auto]">
              <Field label={ROLE_LABEL[role]}>
                {ttsVoices.data && ttsVoices.data.voices.length > 0 ? (
                  <Select value={v.voice} onValueChange={(next) => setVoices((prev) => ({ ...prev, [role]: { ...v, voice: next } }))}>
                    <SelectTrigger aria-label={`${ROLE_LABEL[role]} voice`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(ttsVoices.data.voices.some((tv) => tv.id === v.voice) || !v.voice ? ttsVoices.data.voices : [{ id: v.voice, name: voiceName(v.voice) }, ...ttsVoices.data.voices]).map((tv) => (
                        <SelectItem key={tv.id} value={tv.id}>
                          {tv.name ?? tv.id}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input value={v.voice} aria-label={`${ROLE_LABEL[role]} voice`} onChange={(e) => setVoices((prev) => ({ ...prev, [role]: { ...v, voice: e.target.value } }))} />
                )}
              </Field>
              <Field label="Speed">
                <Input
                  type="number"
                  step={0.05}
                  min={0.5}
                  max={2}
                  value={v.speed}
                  aria-label={`${ROLE_LABEL[role]} speed`}
                  onChange={(e) => setVoices((prev) => ({ ...prev, [role]: { ...v, speed: toFloat(e.target.value) } }))}
                />
              </Field>
              <SampleButton role={role} />
            </div>
          )
        })}
      </div>

      <div className="mt-5 flex items-center gap-2">
        <Button variant="primary" onClick={() => save.mutate()} loading={save.isPending}>
          Save
        </Button>
        <Button
          variant="ghost"
          onClick={() => {
            if (!settings.data) return
            setBands(settings.data.story.age_bands.map((b) => ({ ...b })))
            setVoices({ ...settings.data.story.voices })
            setGenerationMode(settings.data.story.generation_mode ?? 'bones')
          }}
        >
          Undo changes
        </Button>
      </div>
    </section>
  )
}

/** Plays `GET /api/tts/sample?role=` — the wizard's listening check, here too. */
function SampleButton({ role }: { role: VoiceRole }) {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const [busy, setBusy] = useState(false)

  const play = async () => {
    setBusy(true)
    try {
      const res = await fetchJson<{ voice: string; url: string; dur: number }>('/api/tts/sample', { query: { role } })
      const el = audioRef.current ?? new Audio()
      audioRef.current = el
      el.src = res.url
      await el.play()
    } catch (e) {
      toast.error('Could not play a sample', { description: e instanceof Error ? e.message : String(e) })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Button variant="ghost" onClick={() => void play()} loading={busy} aria-label={`Play a sample of the ${ROLE_LABEL[role]} voice`}>
      <Play aria-hidden />
      Listen
    </Button>
  )
}

function toInt(value: string): number {
  const n = Math.round(Number(value))
  return Number.isFinite(n) && n >= 0 ? n : 0
}

function toFloat(value: string): number {
  const n = Number(value)
  return Number.isFinite(n) && n >= 0 ? n : 0
}
